import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Loader2, QrCode, User, Phone, Mail, MapPin, IndianRupee, CheckCircle, XCircle, Camera, ArrowLeft, Coffee, UtensilsCrossed, Gift, Award, Ticket } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECKIN_BUTTONS = [
  { key: 'registered', label: 'Registered', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700' },
  { key: 'breakfast_24', label: 'Breakfast 24/10', icon: Coffee, color: 'bg-amber-600 hover:bg-amber-700' },
  { key: 'lunch_24', label: 'Lunch 24/10', icon: UtensilsCrossed, color: 'bg-orange-600 hover:bg-orange-700' },
  { key: 'dinner_24', label: 'Dinner 24/10', icon: UtensilsCrossed, color: 'bg-rose-600 hover:bg-rose-700' },
  { key: 'breakfast_25', label: 'Breakfast 25/10', icon: Coffee, color: 'bg-amber-600 hover:bg-amber-700' },
  { key: 'lunch_25', label: 'Lunch 25/10', icon: UtensilsCrossed, color: 'bg-orange-600 hover:bg-orange-700' },
  { key: 'kit', label: 'Kit', icon: Gift, color: 'bg-purple-600 hover:bg-purple-700' },
  { key: 'certificate', label: 'Certificate', icon: Award, color: 'bg-blue-600 hover:bg-blue-700' },
];

const ScannerPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [scanning, setScanning] = useState(false);
  const [delegateData, setDelegateData] = useState(null);
  const [scanError, setScanError] = useState('');
  const [checkinLoading, setCheckinLoading] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
        .then(() => { setIsAuthenticated(true); setChecking(false); })
        .catch(() => { localStorage.removeItem('admin_token'); setChecking(false); });
    } else {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('id');
      if (id) lookupRegistration(id);
    }
  }, [isAuthenticated, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { pin });
      if (res.data.token) {
        setToken(res.data.token);
        localStorage.setItem('admin_token', res.data.token);
      }
      setIsAuthenticated(true);
      toast.success('Login successful');
      const params = new URLSearchParams(window.location.search);
      const id = params.get('id');
      if (id) lookupRegistration(id, res.data.token);
    } catch {
      toast.error('Invalid PIN');
    } finally {
      setLoading(false);
    }
  };

  const lookupRegistration = async (registrationId, overrideToken) => {
    setScanError('');
    setDelegateData(null);
    try {
      const headers = overrideToken ? { Authorization: `Bearer ${overrideToken}` } : authHeaders();
      const res = await axios.get(`${API}/registration/${registrationId}`, { headers });
      setDelegateData(res.data);
      stopScanner();
    } catch (err) {
      setScanError(err.response?.status === 404 ? 'Registration not found' : 'Failed to fetch registration');
    }
  };

  const handleCheckin = async (checkinKey) => {
    if (!delegateData) return;
    setCheckinLoading(checkinKey);
    try {
      const res = await axios.post(`${API}/scanner/checkin`, {
        registration_id: delegateData.id,
        checkin_key: checkinKey,
      }, { headers: authHeaders() });
      setDelegateData(prev => ({ ...prev, event_checkins: res.data.event_checkins }));
      toast.success(`${CHECKIN_BUTTONS.find(b => b.key === checkinKey)?.label} - Done!`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Check-in failed';
      toast.error(msg);
    } finally {
      setCheckinLoading('');
    }
  };

  const startScanner = async () => {
    setScanError('');
    setDelegateData(null);
    setScanning(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          let regId = decodedText;
          try {
            const url = new URL(decodedText);
            regId = url.searchParams.get('id') || decodedText;
          } catch { /* not a URL */ }
          lookupRegistration(regId);
        },
        () => {}
      );
    } catch {
      setScanError('Camera access denied or not available');
      setScanning(false);
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const resetScanner = () => {
    setDelegateData(null);
    setScanError('');
    window.history.replaceState({}, '', window.location.pathname);
  };

  const formatAmount = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

  if (checking) {
    return (
      <div className="min-h-screen pt-32 pb-16 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6F4A]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-16" data-testid="scanner-login-page">
        <div className="max-w-sm mx-auto px-6">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-[#FF6F4A] flex items-center justify-center">
              <QrCode className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2">QR <span className="gradient-text">Scanner</span></h1>
            <p className="text-slate-400">AGOC 2026 Badge Verification</p>
          </div>
          <form onSubmit={handleLogin} className="glass-card p-8">
            <div className="space-y-5">
              <div>
                <Label htmlFor="pin" className="text-white">Enter PIN</Label>
                <Input id="pin" type="password" required value={pin} onChange={(e) => setPin(e.target.value)}
                  className="glass-card border-white/20 text-white mt-2 text-center text-2xl tracking-widest"
                  placeholder="Enter PIN" data-testid="scanner-pin-input" autoComplete="off" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold py-5"
                data-testid="scanner-login-button">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                {loading ? 'Verifying...' : 'Login'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Delegate Info + Check-in Buttons
  if (delegateData) {
    const isVerified = delegateData.payment_status === 'completed';
    const checkins = delegateData.event_checkins || {};

    return (
      <div className="min-h-screen pt-32 pb-16" data-testid="delegate-info">
        <div className="max-w-lg mx-auto px-6">
          <div className={`glass-card p-8 ${isVerified ? 'border-2 border-green-500/50' : 'border-2 border-yellow-500/50'}`}>
            <div className="text-center mb-6">
              {delegateData.delegate_photo_url && delegateData.delegate_photo_url !== 'None' && delegateData.delegate_photo_url.includes('/') ? (
                <img src={`${BACKEND_URL}/api/file/${delegateData.delegate_photo_url.split('/').pop()}`}
                  alt={delegateData.full_name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-3 border-white/20"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className={`w-24 h-24 rounded-full mx-auto mb-4 bg-gradient-to-br from-[#FF6F4A] to-[#FFB6C1] items-center justify-center ${delegateData.delegate_photo_url && delegateData.delegate_photo_url !== 'None' && delegateData.delegate_photo_url.includes('/') ? 'hidden' : 'flex'}`}>
                <User className="h-12 w-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white" data-testid="delegate-name">{delegateData.full_name}</h2>
              <div className={`inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-full text-sm font-semibold ${
                isVerified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              }`} data-testid="delegate-status">
                {isVerified ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {isVerified ? 'VERIFIED' : 'PENDING VERIFICATION'}
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 glass-card p-3">
                <Mail className="h-4 w-4 text-[#FF6F4A] flex-shrink-0" />
                <div><p className="text-xs text-slate-400">Email</p><p className="text-sm text-white" data-testid="delegate-email">{delegateData.email}</p></div>
              </div>
              <div className="flex items-center gap-3 glass-card p-3">
                <Phone className="h-4 w-4 text-[#FF6F4A] flex-shrink-0" />
                <div><p className="text-xs text-slate-400">Phone</p><p className="text-sm text-white" data-testid="delegate-phone">{delegateData.phone}</p></div>
              </div>
              <div className="flex items-center gap-3 glass-card p-3">
                <MapPin className="h-4 w-4 text-[#FF6F4A] flex-shrink-0" />
                <div><p className="text-xs text-slate-400">City</p><p className="text-sm text-white" data-testid="delegate-city">{delegateData.city || '-'}</p></div>
              </div>
              <div className="flex items-center gap-3 glass-card p-3">
                <Ticket className="h-4 w-4 text-[#FFB6C1] flex-shrink-0" />
                <div><p className="text-xs text-slate-400">Registration Type</p><p className="text-sm text-[#FF6F4A] font-semibold" data-testid="delegate-type">{delegateData.registration_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p></div>
              </div>
              <div className="flex items-center gap-3 glass-card p-3">
                <IndianRupee className="h-4 w-4 text-[#FFB6C1] flex-shrink-0" />
                <div><p className="text-xs text-slate-400">Amount</p><p className="text-sm text-white font-semibold" data-testid="delegate-amount">{formatAmount(delegateData.amount)}</p></div>
              </div>
            </div>

            {/* Event Check-in Buttons */}
            <div className="border-t border-white/10 pt-5" data-testid="checkin-buttons-section">
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Ticket className="h-4 w-4 text-[#FF6F4A]" /> Event Check-in
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {CHECKIN_BUTTONS.map(btn => {
                  const isChecked = !!checkins[btn.key];
                  const Icon = btn.icon;
                  return (
                    <button
                      key={btn.key}
                      onClick={() => !isChecked && handleCheckin(btn.key)}
                      disabled={isChecked || checkinLoading === btn.key}
                      className={`flex items-center gap-2 px-3 py-3 rounded-lg text-xs font-semibold text-white transition-all ${
                        isChecked
                          ? 'bg-slate-700/50 opacity-60 cursor-not-allowed line-through'
                          : btn.color + ' cursor-pointer'
                      }`}
                      data-testid={`checkin-btn-${btn.key}`}
                    >
                      {checkinLoading === btn.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isChecked ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button onClick={resetScanner}
              className="w-full mt-6 bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white"
              data-testid="scan-another-button">
              <ArrowLeft className="mr-2 h-4 w-4" /> Scan Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Scanner View
  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="scanner-page">
      <div className="max-w-lg mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">QR <span className="gradient-text">Scanner</span></h1>
          <p className="text-slate-400">Scan delegate badge to view details</p>
        </div>
        <div className="glass-card p-6">
          {!scanning ? (
            <div className="text-center py-8">
              <QrCode className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <Button onClick={startScanner}
                className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold px-8 py-5 text-lg"
                data-testid="start-scanner-button">
                <Camera className="mr-2 h-5 w-5" /> Start Camera Scanner
              </Button>
            </div>
          ) : (
            <div>
              <div id="qr-reader" ref={scannerRef} className="rounded-xl overflow-hidden mb-4"></div>
              <Button onClick={stopScanner} variant="outline"
                className="w-full glass-card text-white border-white/20" data-testid="stop-scanner-button">
                Stop Scanner
              </Button>
            </div>
          )}
          {scanError && (
            <div className="mt-4 p-4 glass-card border-red-500/30 text-center">
              <XCircle className="h-6 w-6 text-red-400 mx-auto mb-2" />
              <p className="text-red-400 text-sm">{scanError}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
